import React, { useRef, useEffect, useState, useCallback } from 'react';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { getCategoryImage } from './shopUtils';

const AUTO_SCROLL_PX = 1; // px per tick
const TICK_MS = 30;
const PAUSE_AFTER_INTERACTION_MS = 4000;
const ARROW_SCROLL_PX = 280;

/**
 * Horizontal auto-scrolling carousel of category pills.
 * - Arrows (prev/next) on each side
 * - Auto-advance with seamless loop (clones cats list twice)
 * - Pauses on hover / interaction
 */
const CategoryCarousel = ({
  parentCats, categories, selectedCategory, goCategory,
  setSearchInput, setSelectedCategory, colors, debouncedSearch
}) => {
  const scrollerRef = useRef(null);
  const intervalRef = useRef(null);
  const pauseRef = useRef(false);
  const [isHovered, setIsHovered] = useState(false);

  // Sort cats so "Spécialités, Braséros, Kamados" come first for visibility
  const ordered = (() => {
    const featured = ['specialites', 'braseros', 'kamados', 'special-fetes', 'les-colis', 'nos-viandes-dexception'];
    const featList = featured.map(s => parentCats.find(c => (c.slug || '').includes(s))).filter(Boolean);
    const rest = parentCats.filter(c => !featList.includes(c));
    return [...featList, ...rest];
  })();

  // Duplicate list once to enable seamless circular scroll
  const looped = [...ordered, ...ordered];

  const stopAutoScroll = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startAutoScroll = useCallback(() => {
    stopAutoScroll();
    if (!scrollerRef.current) return;
    intervalRef.current = setInterval(() => {
      const el = scrollerRef.current;
      if (!el || pauseRef.current || isHovered) return;
      el.scrollLeft += AUTO_SCROLL_PX;
      // Loop back when reaching half (clone boundary)
      if (el.scrollLeft >= el.scrollWidth / 2) {
        el.scrollLeft = 0;
      }
    }, TICK_MS);
  }, [isHovered, stopAutoScroll]);

  useEffect(() => {
    startAutoScroll();
    return stopAutoScroll;
  }, [startAutoScroll, stopAutoScroll]);

  const triggerPause = () => {
    pauseRef.current = true;
    setTimeout(() => { pauseRef.current = false; }, PAUSE_AFTER_INTERACTION_MS);
  };

  const scrollBy = (delta) => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollBy({ left: delta, behavior: 'smooth' });
    triggerPause();
  };

  return (
    <div
      className="relative max-w-[1300px] mx-auto px-2 md:px-6"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Prev arrow */}
      <button
        onClick={() => scrollBy(-ARROW_SCROLL_PX)}
        aria-label="Précédent"
        data-testid="carousel-prev"
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center hover:bg-slate-50 transition-colors"
      >
        <CaretLeft size={18} weight="bold" className="text-slate-600" />
      </button>

      <div
        ref={scrollerRef}
        className="flex items-center gap-0 overflow-x-auto scrollbar-hide py-3 px-10"
        onWheel={triggerPause}
        onTouchStart={triggerPause}
        data-testid="category-carousel"
      >
        {/* Universal "All" tab — only once, before the loop */}
        <button
          onClick={() => { setSelectedCategory(''); setSearchInput(''); triggerPause(); }}
          className={`flex-shrink-0 px-5 py-2.5 rounded-full text-[15px] font-bold transition-all mr-2 ${!debouncedSearch && !selectedCategory ? 'text-white shadow-lg' : 'text-slate-500 bg-slate-50 hover:bg-slate-100'}`}
          style={!debouncedSearch && !selectedCategory ? { backgroundColor: colors.primary } : {}}
          data-testid="meat-tab-all"
        >
          Tout
        </button>

        {looped.map((c, idx) => {
          const isActive = selectedCategory === c.id
            || categories.some(ch => ch.parent_id === c.id && ch.id === selectedCategory);
          const img = getCategoryImage(c);
          return (
            <button
              key={`${c.id}-${idx}`}
              onClick={() => { goCategory(c.id); setSearchInput(''); triggerPause(); }}
              className={`flex-shrink-0 flex items-center gap-2.5 px-5 py-2.5 rounded-full text-[15px] font-bold transition-all mr-2 ${isActive ? 'text-white shadow-lg' : 'text-slate-600 bg-slate-50 hover:bg-slate-100'}`}
              style={isActive ? { backgroundColor: colors.primary } : {}}
              data-testid={`carousel-cat-${c.slug || c.id}`}
            >
              {img && <img src={img} alt="" className="w-[36px] h-[36px] rounded-full object-cover border-2 border-white shadow-sm" loading="lazy" />}
              <span className="whitespace-nowrap">{c.name}</span>
            </button>
          );
        })}
      </div>

      {/* Next arrow */}
      <button
        onClick={() => scrollBy(ARROW_SCROLL_PX)}
        aria-label="Suivant"
        data-testid="carousel-next"
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center hover:bg-slate-50 transition-colors"
      >
        <CaretRight size={18} weight="bold" className="text-slate-600" />
      </button>

      {/* Side fade masks */}
      <div className="pointer-events-none absolute left-9 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent" />
      <div className="pointer-events-none absolute right-9 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent" />
    </div>
  );
};

export default CategoryCarousel;
